import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { AttentionItem } from '../components/AttentionItem.jsx';
import { PageWelcome } from '../components/PageWelcome.jsx';
import { ProgressBar } from '../components/ProgressBar.jsx';
import { StatusBadge } from '../components/StatusBadge.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import { projectHealth } from '../utils/statusUi.js';
import { stageCaption } from '../utils/taskProgress.js';
import { firstName } from '../utils/userDisplay.js';

export function MyDashboardPage() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiJson('/api/my/dashboard')
      .then(setStats)
      .catch((e) => setError(e.message));
  }, []);

  const featured = useMemo(() => {
    if (!stats) return null;
    return stats.project_progress?.[0] || stats.recent_projects?.[0] || null;
  }, [stats]);

  const actions = useMemo(() => {
    if (!stats) return [];
    const items = [];
    const pendingForms = stats.questionnaire_status?.forms_pending || 0;
    const pendingAssets = stats.asset_requests?.pending || 0;
    const openTasks = stats.client_tasks?.open || 0;

    if (caps.view_client_assets && pendingAssets > 0) {
      items.push({
        tone: 'danger',
        title: 'Upload requested files',
        detail: `${pendingAssets} asset request${pendingAssets === 1 ? '' : 's'} waiting on you`,
        to: '/my/assets',
        action: 'Upload files',
      });
    }
    if (caps.view_client_faq && pendingForms > 0) {
      items.push({
        tone: 'warning',
        title: 'Complete website content form',
        detail: `${pendingForms} form${pendingForms === 1 ? '' : 's'} still pending`,
        to: '/my/forms',
        action: 'Complete form',
      });
    }
    if (caps.view_client_tasks && openTasks > 0) {
      const recent = stats.client_tasks?.recent || [];
      const today = new Date().toISOString().slice(0, 10);
      if (recent.length) {
        for (const task of recent) {
          const due = task.due_date ? String(task.due_date).slice(0, 10) : '';
          items.push({
            tone: due && due < today ? 'danger' : 'info',
            title: task.title,
            detail: task.project?.name || 'Client request',
            to: `/my/tasks?task=${task.id}`,
            action: 'Open',
          });
        }
      } else {
        items.push({
          tone: 'info',
          title: 'Review open requests',
          detail: `${openTasks} open request${openTasks === 1 ? '' : 's'} assigned to you`,
          to: '/my/tasks',
          action: 'Open',
        });
      }
    }
    return items;
  }, [stats, caps]);

  if (error) {
    return <p className="err">{error}</p>;
  }

  if (!stats) {
    return <p className="muted">Loading dashboard…</p>;
  }

  const branding = stats.branding;
  const time = stats.time_balance;
  const nearTimeLimit = time && time.time_limit > 0 && time.percent_used >= 80;
  const stages = featured?.stages || [];
  const progress = featured?.progress_percent ?? 0;
  const health = featured ? projectHealth(featured) : null;
  const onTrack = actions.filter((a) => a.tone === 'danger').length === 0;
  const uploads = stats.recent_uploads || [];

  return (
    <div>
      <PageWelcome
        title={`Welcome back, ${firstName(user?.name)}`}
        subtitle={
          stats.client_name
            ? `Here’s where your ${stats.client_name} project stands.`
            : `Here’s where your work with ${branding?.name || 'your agency'} stands.`
        }
      />

      {stats.priority_support ? (
        <p className="panel" style={{ marginBottom: '1rem', borderLeft: '4px solid var(--primary)' }}>
          Priority support is active for your account.
        </p>
      ) : null}

      {!user?.client_id ? (
        <div className="panel">
          <p style={{ margin: 0 }}>
            Your account is not linked to a client yet. Ask {branding?.name || 'your agency'} to attach
            you to a client record.
          </p>
        </div>
      ) : (
        <>
          {featured ? (
            <div className="project-hero" style={{ marginTop: '0.25rem' }} data-tour="tour-dashboard-stats">
              <div className="project-hero-row">
                <div className="project-hero-identity">
                  <div className="project-hero-mark" aria-hidden>
                    {(featured.name || 'P').slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <div className="project-hero-title-row">
                      <h3 className="project-hero-title" style={{ fontSize: '1.15rem' }}>
                        <Link to={`/my/projects/${featured.id}`}>{featured.name}</Link>
                      </h3>
                      {health ? <StatusBadge tone={health.tone} label={health.detail} /> : <StatusBadge status={featured.status} />}
                    </div>
                    <p className="muted small" style={{ margin: 0 }}>
                      {stats.client_name || branding?.name || 'Your project'}
                    </p>
                  </div>
                </div>
              </div>
              <ProgressBar value={progress} tone="info" label={`${progress}% complete`} />
              <div className="project-hero-meta">
                <div>
                  <span className="eyebrow">Current stage</span>
                  <strong>{featured.current_stage || stages.find((s) => s.state === 'current')?.name || '—'}</strong>
                </div>
                <div>
                  <span className="eyebrow">Next stage</span>
                  <strong>{featured.next_stage || stages.find((s) => s.state === 'upcoming')?.name || '—'}</strong>
                </div>
                <div>
                  <span className="eyebrow">Target completion</span>
                  <strong>{featured.due_date ? formatDate(featured.due_date) : 'TBD'}</strong>
                </div>
              </div>
            </div>
          ) : (
            <div className="panel" data-tour="tour-dashboard-stats">
              <p className="empty-state" style={{ margin: 0 }}>
                Project overview will appear once a project is assigned.
              </p>
            </div>
          )}
        </>
      )}

      <div className="dash-grid">
        <section className="panel">
          <div className="panel-head">
            <h3 className="panel-title">
              {actions.length
                ? `We need ${actions.length} thing${actions.length === 1 ? '' : 's'} from you`
                : 'You’re all caught up'}
            </h3>
          </div>
          {actions.length ? (
            <div className="attention-list">
              {actions.map((item) => (
                <AttentionItem
                  key={`${item.to}-${item.title}`}
                  tone={item.tone}
                  title={item.title}
                  detail={item.detail}
                  to={item.to}
                  action={<span className={`action-cta action-cta--${item.tone}`}>{item.action}</span>}
                />
              ))}
            </div>
          ) : (
            <p className="empty-state">Nothing is waiting on you right now.</p>
          )}
        </section>

        <section className="panel">
          <h3 className="panel-title">Project Progress</h3>
          {stages.length ? (
            <ol className="timeline-stages">
              {stages.map((stage) => (
                <li key={stage.key || stage.name}>
                  <span className={`timeline-dot timeline-dot--${stage.state}`} />
                  <strong>{stage.name}</strong>
                  <div className="muted small">{stageCaption(stage)}</div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="empty-state">Project timeline will appear once a project is assigned.</p>
          )}
        </section>
      </div>

      <div className="dash-grid">
        <section className="panel">
          <h3 className="panel-title">Send files to {branding?.name || 'ViiB'}</h3>
          <Link className="dropzone" to={caps.view_client_assets ? '/my/assets' : '/my/files'}>
            <strong>Drag & drop</strong>
            <span className="muted small">or browse files to send to {branding?.name || 'your agency'}</span>
          </Link>
          {uploads.length ? (
            <div style={{ marginTop: '1rem' }}>
              <h3 className="panel-title">Recently uploaded</h3>
              {uploads.map((file) => (
                <div key={file.id} className="file-row">
                  <span className="file-glyph">{fileExt(file.file_name)}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.file_name}</strong>
                    <span className="muted small">{file.created_at ? formatDateTime(file.created_at) : ''}</span>
                  </span>
                  <StatusBadge tone="success" label="Uploaded" dot={false} />
                </div>
              ))}
            </div>
          ) : null}
        </section>

        <section className="panel help-card">
          <div className="help-icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a8.5 8.5 0 0 1-8.5 8.5H8l-4 3v-5.2A8.5 8.5 0 1 1 21 12z" strokeLinejoin="round" />
            </svg>
          </div>
          <h3 className="panel-title">Need help?</h3>
          <p className="muted small">
            Chat with the {branding?.name || 'ViiB'} team whenever you need a hand.
          </p>
          {stats.support_email ? (
            <a className="btn-primary" href={`mailto:${stats.support_email}`} style={{ display: 'inline-block', marginTop: '0.5rem' }}>
              Chat with {branding?.name || 'ViiB'}
            </a>
          ) : (
            <p className="muted small">Live chat is available from the corner of this page when enabled.</p>
          )}
        </section>
      </div>

      {stats.upcoming_meetings?.length ? (
        <section className="panel" style={{ marginTop: '1rem' }}>
          <div className="panel-head">
            <h3 className="panel-title">Upcoming meetings</h3>
            <Link to="/my/meetings" className="panel-link">
              All meetings →
            </Link>
          </div>
          <ul className="feed-list">
            {stats.upcoming_meetings.map((m) => (
              <li key={m.id}>
                <span>
                  <strong>{m.subject}</strong>
                  {m.join_url ? (
                    <>
                      {' · '}
                      <a href={m.join_url} target="_blank" rel="noreferrer">
                        Join
                      </a>
                    </>
                  ) : null}
                </span>
                <span className="muted small">{m.start_at ? formatDateTime(m.start_at) : ''}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {stats.announcements?.length ? (
        <section className="panel" style={{ marginTop: '1rem' }} data-tour="dashboard-news">
          <h3 className="panel-title">Company news</h3>
          <ul className="feed-list">
            {stats.announcements.map((a) => (
              <li key={a.id}>
                <span>
                  <strong>{a.title}</strong>
                  {a.body ? <div className="muted small">{a.body}</div> : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {caps.view_time_balance && time?.time_limit > 0 ? (
        <section className="panel" style={{ marginTop: '1rem' }} data-tour="dashboard-time">
          <h3 className="panel-title">Time balance</h3>
          <p className="muted small" style={{ margin: 0 }}>
            {time.current_time}h used of {time.time_limit}h
            {nearTimeLimit ? ' · approaching your limit' : ''}
            {time.auto_reset_date ? ` · resets ${formatDate(time.auto_reset_date)}` : ''}
          </p>
        </section>
      ) : (
        <span data-tour="dashboard-time" hidden />
      )}

      <div className="track-banner">
        <span>
          {onTrack
            ? 'Your project is on track! We’re working hard behind the scenes.'
            : 'A few items still need your attention.'}
        </span>
      </div>
    </div>
  );
}

function fileExt(name) {
  const part = String(name || '').split('.').pop();
  return (part || 'FILE').slice(0, 4).toUpperCase();
}
